import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Skeleton,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import MainHeading from "../../../components/main-heading.component";
import { _get, _post } from "../../../services/api.service";
import { lockScroll, unlockScroll } from "../../../hooks/ScrollLockContext";
import "../content-engine.css";
import { contentEngineService } from "../content-engine.service";
import "../../video-create/CreateVideo.css";
import "./ChooseScript.css";
import FormatCard from "./format-cards/FormatCard";

interface AttributeField {
  key: string;
  label: string;
  description: string;
  options: { value: string; label: string }[];
}

const ChooseScript: React.FC = () => {
  const navigate = useNavigate();
  const steps = [
    { label: "Topic", description: "Enter your topic idea", path: "/content-engine" },
    { label: "Format", description: "Choose your content format", path: "/content-engine/select-format" },
    { label: "Scripts", description: "Pick from generated scripts", path: "/content-engine/choose-script" },
    { label: "Refinement", description: "Refine and finalize your script", path: "/content-engine/script-detail" },
  ];
  const currentStep = 2;
  const [scripts, setScripts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [topic, setTopic] = useState("");
  const [topicId, setTopicId] = useState<number | null>(null);
  const [attributes, setAttributes] = useState<Record<string, string>>({});
  const [attributesPanelOpen, setAttributesPanelOpen] = useState(false);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [fields, setFields] = useState<AttributeField[]>([]);
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState(false);
  const [newCreativeIds, setNewCreativeIds] = useState<Set<number>>(contentEngineService.getNewCreativeIds());
  const fetched = useRef(false);

  useEffect(() => {
    if (attributesPanelOpen || generating) lockScroll();
    else unlockScroll();
    return () => unlockScroll();
  }, [attributesPanelOpen, generating]);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    const selectedTopic = contentEngineService.getSelectedTopic();
    if (!selectedTopic) {
      setLoading(false);
      navigate("/content-engine", { replace: true });
      return;
    }
    (async () => {
      try {
        setTopic(selectedTopic.name);
        setTopicId(selectedTopic.id);

        const selectedFormat = contentEngineService.getSelectedContentFormat();
        const generated = contentEngineService.getGeneratedContents(selectedTopic.id);

        if (generated) {
          setScripts(generated);
          const cachedAttrs = contentEngineService.getCachedAttributes(selectedTopic.id);
          if (cachedAttrs) setAttributes(cachedAttrs);
          setNewCreativeIds(contentEngineService.getNewCreativeIds());
        } else if (selectedFormat) {
          setGenerating(true);
          const payload = {
            topic_id: selectedTopic.id,
            content_format_id: selectedFormat.id,
          };
          const response = await _post("/content-engine/contents/generate/", payload, {}, false, true);
          const contents = response?.contents || [];
          const responseDirectives = response?.directives || {};
          contentEngineService.setGeneratedContents(selectedTopic.id, contents);
          contentEngineService.setCachedAttributes(selectedTopic.id, responseDirectives);
          setScripts(contents);
          setAttributes(responseDirectives);
          contentEngineService.clearNewCreativeIds();
          setNewCreativeIds(new Set());
          setGenerating(false);
        } else {
          const payload = { topic_id: selectedTopic.id };
          const response = await _post("/content-engine/contents/generate/", payload, {}, false, true);
          const contents = response?.contents || [];
          const responseDirectives = response?.directives || {};
          contentEngineService.setGeneratedContents(selectedTopic.id, contents);
          contentEngineService.setCachedAttributes(selectedTopic.id, responseDirectives);
          setScripts(contents);
          setAttributes(responseDirectives);
          contentEngineService.clearNewCreativeIds();
          setNewCreativeIds(new Set());
        }
      } catch (error) {
        console.error("Error fetching scripts", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const displayTopic = topic.charAt(0).toUpperCase() + topic.slice(1).toLowerCase();

  const handleScriptSelect = (script: any) => {
    if (loading) return;
    contentEngineService.setSelectedScript(script);
    _post(`/content-engine/contents/${script.id}/select-script/`, {}, {}, false, true).catch(() => {});
    navigate("/content-engine/script-detail");
  };

  const handleGenerateMore = async () => {
    setAttributesLoading(true);
    setAttributesPanelOpen(true);
    try {
      const response = await _get("/content-engine/directives/", {}, true);
      const fieldsData = response?.fields || [];
      const defaults = response?.defaults || {};
      setFields(fieldsData);
      const preSelected: Record<string, string> = {};
      for (const field of fieldsData) {
        preSelected[field.key] = attributes[field.key] || defaults[field.key] || field.options[0]?.value || "";
      }
      setSelectedValues(preSelected);
    } catch (error) {
      console.error("Error fetching directives", error);
      setAttributesPanelOpen(false);
    } finally {
      setAttributesLoading(false);
    }
  };

  const handleValueChange = (key: string, value: string) => {
    setSelectedValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleRegenerate = async () => {
    setAttributesPanelOpen(false);
    setGenerating(true);
    try {
      const selectedFormat = contentEngineService.getSelectedContentFormat();
      const payload = {
        topic_id: topicId,
        content_format_id: selectedFormat?.id,
        directives: selectedValues,
      };
      const response = await _post("/content-engine/contents/generate/", payload, {}, false, true);
      const newContents = response?.contents || [];
      const responseDirectives = response?.directives || {};
      const allContents = [...newContents, ...scripts];
      if (topicId) {
        contentEngineService.setGeneratedContents(topicId, allContents);
        contentEngineService.setCachedAttributes(topicId, selectedValues);
      }
      setScripts(allContents);
      setAttributes(responseDirectives);
      const ids: Set<number> = new Set(newContents.map((c: any) => c.id));
      contentEngineService.setNewCreativeIds(ids);
      setNewCreativeIds(ids);
    } catch (error) {
      console.error("Error generating more options", error);
    } finally {
      setGenerating(false);
    }
  };

  return (<>
    <div className="container-fluid choose-script-page">
      <MainHeading
        subTitle="Click a script to preview and refine it"
        title={
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
            {displayTopic}
            {(() => {
              const formatName = contentEngineService.getSelectedContentFormat()?.name
                || (topicId !== null ? contentEngineService.getGeneratedContents(topicId)?.[0]?.content_format?.name : null);
              return formatName ? (
                <sup style={{ lineHeight: 1 }}>
                  <Chip
                    label={formatName.toUpperCase()}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 700,
                      borderRadius: "4px",
                      bgcolor: "rgba(35, 100, 232, 0.1)",
                      color: "#2364e8",
                      "& .MuiChip-label": { px: 0.6 },
                    }}
                  />
                </sup>
              ) : null;
            })()}
          </span>
        }
      />
      <div className="cv-workspace">
        <aside className="cv-progress-panel" aria-label="Content engine progress">
          <div className="cv-progress-caption">CONTENT ENGINE</div>
          {steps.map((step, index) => {
            const complete = index < currentStep;
            const active = index === currentStep;
            return (
              <div
                key={step.label}
                className={`cv-progress-step${complete ? " complete" : ""}${active ? " active" : ""}${complete ? " clickable" : ""}`}
                onClick={() => complete && navigate(step.path)}
                onKeyDown={(e) => complete && e.key === "Enter" && navigate(step.path)}
                role={complete ? "button" : undefined}
                tabIndex={complete ? 0 : undefined}
              >
                <div className="cv-progress-rail">
                  <span>{complete ? "✓" : index + 1}</span>
                </div>
                <div>
                  <div className="cv-progress-title">{step.label}</div>
                  <div className="cv-progress-description">{step.description}</div>
                </div>
              </div>
            );
          })}
        </aside>

        <section className="cv-creator-card">
          <div className="cv-card-inner">
            <div className="choose-header">
              <div>
                <div className="choose-section-title">Choose a style</div>
                <p className="choose-subtitle">Each option approaches the topic differently</p>
              </div>
              <button
                className="cv-btn cv-btn-secondary"
                onClick={handleGenerateMore}
                disabled={loading}
              >
                <span className="generate-btn-content">
                  {loading ? (
                    <>
                      <span className="generate-icon spinning">↻</span> Generating...
                    </>
                  ) : (
                    <>
                      <span className="generate-icon">↻</span> Generate more options
                    </>
                  )}
                </span>
              </button>
            </div>

            <div className="options-cards">
              {scripts.map((content, index) => (
                <div
                  key={index}
                  className={"option-card" + (loading ? " option-card--disabled" : "")}
                  onClick={() => handleScriptSelect(content)}
                >
                  <div className="angle">
                    <span>{content.tag_display}</span>
                    {newCreativeIds.has(content.id) && <span className="new-badge">NEW</span>}
                    {(() => {
                      const blocks = content?.latest_version?.blocks || [];
                      const total = blocks.reduce((s: number, b: any) => s + (parseFloat(b.estimated_duration) || 0), 0);
                      const secs = Math.round(total);
                      return secs > 0 ? (
                        <span
                          title="Approximate video duration"
                          style={{
                            marginLeft: "auto",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#94a3b8",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            cursor: "default",
                            textTransform: "none",
                          }}
                        >
                          ⏱ ~{secs} sec
                        </span>
                      ) : null;
                    })()}
                  </div>
                  <FormatCard content={content} />
                  {(() => {
                    const ts = content.updated_at || content.created_at;
                    if (!ts) return null;
                    const d = new Date(ts);
                    const now = new Date();
                    const diffMs = now.getTime() - d.getTime();
                    const diffMin = Math.floor(diffMs / 60000);
                    const diffHrs = Math.floor(diffMin / 60);
                    const diffDays = Math.floor(diffHrs / 24);
                    let label: string;
                    if (diffMin < 1) label = "just now";
                    else if (diffMin < 60) label = `${diffMin}m ago`;
                    else if (diffHrs < 24) label = `${diffHrs}h ago`;
                    else if (diffDays < 7) label = `${diffDays}d ago`;
                    else label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                    return (
                      <div className="option-card-time">{label}</div>
                    );
                  })()}
                </div>
              ))}
              {loading && scripts.length === 0 && (
                <div className="skeleton-wrap">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rounded" height={120} sx={{ mb: 2 }} />
                  ))}
                </div>
              )}
              {!loading && scripts.length === 0 && (
                <div className="hint">No script options found</div>
              )}
            </div>

            <div className="choose-tip">
              <LightbulbOutlinedIcon sx={{ fontSize: 15, mr: 0.5, verticalAlign: "text-top" }} />{" "}
              Select any script to continue to refinement
            </div>
          </div>
        </section>
      </div>
    </div>

    <Drawer
      className="cs-prefs-drawer"
      anchor="right"
      open={attributesPanelOpen}
      onClose={() => !attributesLoading && setAttributesPanelOpen(false)}
      disableScrollLock
    >
      <div className="cs-prefs-header">
        <div className="cs-prefs-header-inner">
          <div className="cs-prefs-header-left">
            <div className="cs-prefs-icon-wrap">
              <TuneIcon sx={{ fontSize: 24 }} />
            </div>
            <div>
              <h2 className="cs-prefs-title">Generation Preferences</h2>
              <span className="cs-prefs-subtitle">Adjust each attribute to tailor the output</span>
            </div>
          </div>
          <IconButton
            className="cs-prefs-close"
            onClick={() => setAttributesPanelOpen(false)}
            size="small"
            aria-label="Close preferences"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>

      <div className="cs-prefs-body">
        {attributesLoading ? (
          <Box sx={{ py: 2 }}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 3, borderRadius: 3, bgcolor: "rgba(35, 100, 232, 0.06)" }} />
            ))}
          </Box>
        ) : (
          <>
            {fields.map((field) => (
              <div key={field.key} className="cs-prefs-field">
                <div className="cs-prefs-field-head">
                  <span className="cs-prefs-field-label">{field.label}</span>
                  <span className="cs-prefs-field-desc">{field.description}</span>
                </div>
                <div className="cs-prefs-chips">
                  {field.options.map((opt) => {
                    const active = selectedValues[field.key] === opt.value;
                    return (
                      <Chip
                        key={opt.value}
                        label={opt.label}
                        clickable
                        className={`cs-prefs-chip${active ? " active" : ""}`}
                        icon={active ? <CheckIcon sx={{ fontSize: 15, color: "#2364e8 !important" }} /> : undefined}
                        onClick={() => handleValueChange(field.key, opt.value)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="cs-prefs-footer">
        <Button
          className="cs-prefs-btn-cancel"
          onClick={() => setAttributesPanelOpen(false)}
          disabled={attributesLoading}
        >
          Cancel
        </Button>
        <Button
          className="cs-prefs-btn-regenerate"
          variant="contained"
          onClick={handleRegenerate}
          disabled={attributesLoading}
          startIcon={<AutoAwesomeIcon sx={{ fontSize: 17 }} />}
        >
          Regenerate Scripts
        </Button>
      </div>
    </Drawer>

    {generating && createPortal(
      <div className="generate-overlay">
        <div className="generate-overlay-content">
          <div className="generate-overlay-spinner" />
          <div className="generate-overlay-text">Generating your scripts...</div>
        </div>
      </div>,
      document.body
    )}
  </>);
};

export default ChooseScript;
